import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,   SectionTitle, Result, CorrectionBox, MutedText, Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider, Separator, ColoredText} from '../../../../components/course/CourseComponents';
import repereImage from '../repere.jpg';
import distImage from '../dist.jpg';

const Chapitre4Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Ajout d'un écran de début et de fin, et équilibrage du jeu.
        </p>
      </InfoBox>      


      <Separator type="text" text="Écran de début et de fin" color="gray-darker" size="md" />
      <SectionTitle>🎮 1/ Écran de début et de fin</SectionTitle>

      <p>Améliorons l'expérience utilisateur en ajoutant un écran de début et de fin de partie.
      Pour cela, on utilise la variable <InlineCode>status</InlineCode>.</p>

      <ul className="list-disc ml-6 mb-4">
        <li>Si <InlineCode>status == "start"</InlineCode>, alors on affiche notre écran de démarrage.</li>
        <li>Lorsque le joueur clique sur le canvas, on passe <InlineCode>status</InlineCode> à <InlineCode>"play"</InlineCode>.</li>
        <li>Si <InlineCode>status == "play"</InlineCode>, on affiche notre jeu tel qu'il est codé actuellement.</li>
        <li>Lorsque le timer est inférieur ou égal à 0, on passe <InlineCode>status</InlineCode> à <InlineCode>"gameover"</InlineCode>.</li>
        <li>Si <InlineCode>status == "gameover"</InlineCode>, alors on affiche notre écran de fin, avec le score en gros.</li>
      </ul>

      <Note type="exercice" title="🧪 Ecran de début et de fin :">
        Tout va se jouer dans la fonction <InlineCode>draw</InlineCode>.<br/>
        <br/>

      - Écran de jeu : status == "play"
      <p>Autour de notre gameplay actuel, on ajoute :</p>

      <CodeBlock language="javascript">
{`background(9, 26, 52);

//En jeu
if (status == "play") {
  // Gameplay actuel, qui englobe tout ce qu'il y a dans draw
}`}
      </CodeBlock>
      <br/>
      - Écran de début : status == "start"
      <p>En dessous de notre grand if, on ajoute un second if qui affiche le nom du jeu avec un petit émoji :</p>

      <CodeBlock language="javascript">
{`// Écran de début
if (status == "start") {
  textAlign(CENTER);
  textSize(40);
  fill(255, 255, 255);
  text("🎯 Améliore ta visée !", width / 2, height / 2 - 40);
  textSize(20);
  text("Clique pour jouer", width / 2, height / 2);
}`}
      </CodeBlock>
      <br/>
      - Écran de fin : status == "gameover"
      <p>En dessous de notre second if, on ajoute un troisième if qui affiche "Temps écoulé" et notre score en gros :</p>

      <CodeBlock language="javascript">
{`// Écran de fin
if (status == "gameover") {
  textAlign(CENTER);
  textSize(20);
  fill(255, 255, 255);
  text("⏰ Temps écoulé !", width / 2, height / 2 - 40);
  textSize(40);
  text("Score: " + score, width / 2, height / 2);
}`}
      </CodeBlock>

      <br/>
      <Result>▶️ Tu devrais voir un écran de début et un écran de fin!</Result>
      <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`function draw() {
  background(9, 26, 52);

  //En jeu
  if (status == "play") {
    // Cercle
    fill(34, 174, 220);
    circle(cercle_x, cercle_y, cercle_rayon * 2);

    // Score
    fill(242, 177, 67);
    textSize(30);
    textAlign(LEFT);
    text("Score: " + score, 10, 30);

    // Timer
    timer = timer - deltaTime / 1000;
    fill(242, 177, 67);
    textAlign(RIGHT);
    text("Temps: " + ceil(timer), 590, 30);

    // Gameover
    if (timer <= 0) {
      timer = 0;
      status = "gameover";
    }
  }

  // Écran de début
  if (status == "start") {
    textAlign(CENTER);
    textSize(40);
    fill(255, 255, 255);
    text("🎯 Améliore ta visée !", width / 2, height / 2 - 40);
    textSize(20);
    text("Clique pour jouer", width / 2, height / 2);
  }
  
  // Écran de fin
  if (status == "gameover") {
    textAlign(CENTER);
    textSize(20);
    fill(255, 255, 255);
    text("⏰ Temps écoulé !", width / 2, height / 2 - 40);
    textSize(40);
    text("Score: " + score, width / 2, height / 2);
  }
}
`}
        </CodeBlock>
      </CorrectionBox>

      </Note>

      <Note type="tip" title="⚠️ Dernière correction importante :">
        Il reste un dernier changement à faire : lorsque l'écran de fin apparaît, il est encore possible de cliquer sur le cercle, même si on ne le voit pas.<br/>
        Il faut corriger cela en quittant la fonction <InlineCode>mousePressed</InlineCode> si le status est <InlineCode>"gameover"</InlineCode>.
      </Note>

      <Note type="exercice" title="🧪 Retravail de mousePressed :">
        Au début de la fonction <InlineCode>mousePressed()</InlineCode>, ajoute cette condition pour empêcher de cliquer lorsque status est <InlineCode>"gameover"</InlineCode>
        <CodeBlock language="javascript">
{`if (status == "gameover") return;`}
        </CodeBlock>

        La fonction <InlineCode>return</InlineCode> permet de quitter immédiatement la fonction sans exécuter le code qui suit.<br/>
        Ainsi, le score ne sera plus augmenté si le jeu n'est pas en cours.

        <br/><br/>
        <Result>▶️ Tu ne peux plus cliquer sur le cercle lorsque le jeu est fini.</Result>

        <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`function mousePressed() {
  if (status == "gameover") return;
  if (status == "start") status = "play";
  
  let distance = dist(mouseX, mouseY, cercle_x, cercle_y);
  if (distance < cercle_rayon) {
    setCirclePosition();
    score = score + 1;
  }
}`}
        </CodeBlock>
      </CorrectionBox>
      </Note>

     

      <Separator type="text" text="Équilibrage" color="gray-darker" size="md" />
      <SectionTitle>⚖️ 2/ Équilibrage</SectionTitle>

      <p>Voilà, tu viens de terminer ton premier jeu !!<br/>
      Il reste une dernière étape importante : l'équilibrage du jeu.</p>

      <p>C'est une étape essentielle, car un jeu mal équilibré peut vite devenir inintéressant.<br/>
      Le jeu étant plutôt simple, les seules variables qui permettent de l'équilibrer sont :</p>

      <SubHeading>⏱️ Le timer</SubHeading>
      <p>Tu peux le réduire ou l'augmenter selon ton ressenti. Est-ce que 20 secondes c'est trop long ? Pas assez ?<br/>
      En haut de ton code, tu peux changer la valeur de la variable timer :</p>

      <CodeBlock language="javascript">
{`let timer = 25;`}
      </CodeBlock>

      <SubHeading>🎯 La taille du cercle</SubHeading>
      <p>
      Si tu trouves le jeu trop facile, réduis la taille du cercle pour affiner ta visée.<br/>
      Au contraire, si tu as du mal à cliquer sur le cercle, agrandis-le :</p>

      <CodeBlock language="javascript">
{`let cercle_rayon = 30;`}
      </CodeBlock>

      <Separator type="text" text="Code final" color="gray-darker" size="md" />
      <SectionTitle>✅ 3/ Code final</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 1 : Améliore ta visée

// Équilibrage
let timer = 25;
let cercle_rayon = 30;

let cercle_x = 0;
let cercle_y = 0;
let score = 0;
let status = "start"; // ou "play" ou "gameover"

function setup() {
  createCanvas(600, 600);
  setCirclePosition();
}

function draw() {
  background(9, 26, 52);

  //En jeu
  if (status == "play") {
    // Cercle
    fill(34, 174, 220);
    circle(cercle_x, cercle_y, cercle_rayon * 2);

    // Score
    fill(242, 177, 67);
    textSize(30);
    textAlign(LEFT);
    text("Score: " + score, 10, 30);

    // Timer
    timer = timer - deltaTime / 1000;
    fill(242, 177, 67);
    textAlign(RIGHT);
    text("Temps: " + ceil(timer), 590, 30);

    // Gameover
    if (timer <= 0) {
      timer = 0;
      status = "gameover";
    }
  }

  // Écran de début
  if (status == "start") {
    textAlign(CENTER);
    textSize(40);
    fill(255, 255, 255);
    text("🎯 Améliore ta visée !", width / 2, height / 2 - 40);
    textSize(20);
    text("Clique pour jouer", width / 2, height / 2);
  }
  
  // Écran de fin
  if (status == "gameover") {
    textAlign(CENTER);
    textSize(20);
    fill(255, 255, 255);
    text("⏰ Temps écoulé !", width / 2, height / 2 - 40);
    textSize(40);
    text("Score: " + score, width / 2, height / 2);
  }
}

function mousePressed() {
  if (status == "gameover") return;
  if (status == "start") status = "play";

  let distance = dist(mouseX, mouseY, cercle_x, cercle_y);
  if (distance < cercle_rayon) {
    setCirclePosition();
    score = score + 1;
  }
}

function setCirclePosition() {
  cercle_x = random(50, 550);
  cercle_y = random(50, 550);
}`}
      </CodeBlock>

      <Note type="info" title="🎉 Félicitations !">
        Voilà, le cours est maintenant terminé !<br/>
        J'espère que ça t'a plu. On se retrouve très vite pour coder un second jeu, un peu plus complexe et amusant !<br/><br/>
        <strong>PS :</strong> Avec un timer à 25s et un cercle de 30px de rayon, mon highscore est de <strong>52</strong> ! Et toi ? 😄
      </Note>
   
    </>
  );
};

export default Chapitre4Content; 